import { useState } from "react";

import { toISODateTime } from "common/utils/dates";
import { SubscriptionFrequency, UserSubscriptionAction } from "common/types";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import Calendar from "client/common/components/calendars/Calendar";
import { useCurrentDrawer } from "client/common/hooks/stackables";

import { useUpdateSubscriptionMutation } from "../../hooks";
import { ClientSubscription } from "../../types";
import SubscriptionFrequencySelect from "../SubscriptionFrequencySelect";

export type SubscriptionResumeDrawerProps = {
  subscription: ClientSubscription;
};

// TODO(HD2-981): make EDB-aware on v2 (AUTOSHIP_EDB) — relabel to "Select your next estimated
// delivery date" and submit expected_delivery_by; v2 RESUME is rejected without it
// (validate_resume_action_dates).
const SubscriptionResumeDrawer = ({ subscription }: SubscriptionResumeDrawerProps) => {
  const { close } = useCurrentDrawer();
  const {
    mutate: updateSubscription,
    isLoading,
    error,
  } = useUpdateSubscriptionMutation(subscription.id, {
    onSuccess: close,
  });

  const [nextOrderDate, setNextOrderDate] = useState(new Date());
  const [frequencyValues, setFrequencyValues] = useState<SubscriptionFrequency>(
    subscription.is_active_frequency_valid
      ? subscription.active_frequency
      : subscription.recommended_frequency,
  );

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title="Resume autoship"
        description="Enable your Autoship again by selecting your next order date and frequency"
        onClose={close}
      />
      <DrawerLayout.Form
        size="l"
        canSubmit={true}
        onSubmit={() =>
          updateSubscription({
            action: UserSubscriptionAction.Resume,
            next_order_date: toISODateTime(nextOrderDate),
            ...frequencyValues,
          })
        }
        submitText="Resume autoship"
        isLoading={isLoading}
        error={error}
        fieldErrors={[
          error?.errors.action,
          error?.errors.next_order_date,
          error?.errors.frequency,
          error?.errors.interval_unit,
        ]}
      >
        <DrawerLayout.Form.Group>
          <DrawerLayout.Form.Label>Select your next order date</DrawerLayout.Form.Label>
          <Calendar
            disabledDays={{ before: new Date() }}
            selectedDays={nextOrderDate}
            onSelect={setNextOrderDate}
          />
        </DrawerLayout.Form.Group>
        <DrawerLayout.Form.Group>
          <DrawerLayout.Form.Label>Select your order frequency</DrawerLayout.Form.Label>
          <SubscriptionFrequencySelect
            subscription={subscription}
            value={frequencyValues}
            onSelect={setFrequencyValues}
          />
        </DrawerLayout.Form.Group>
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

export default SubscriptionResumeDrawer;
